import { FC, Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, types } from "@vilocnv/allsetra-core";
import ObjectRideDetailsDrawer from "components/common/objects/ObjectRideDetailsDrawer/ObjectRideDetailsDrawer";
import ObjectRideGoogleMap from "components/maps/ObjectRideGoogleMap/ObjectRideGoogleMap";
import { useTranslation } from "react-i18next";

// Data
import { isEmpty } from "lodash";
import {
  useAppDispatch,
  useAppSelector,
  useResolveAddress,
  hasRideHiddenAddress,
  useRidePermissions
} from "hooks";
import {
  getAddressThunk,
  getObjectRideLocationsThunk,
  getObjectSpecificRideInfoThunk
} from "app/features";
import {
  selectDrawerSelectedAccountId,
  selectObjectRidesState
} from "app/data/selectors";
import { PageLoader } from "@vilocnv/allsetra-core";
import { Grid } from "@mui/material";
import { MapStateProvider } from "components/maps/Map/v2/GoogleMap/MapStateContext";
import {
  buildMixedRideTimelineStops,
  getMixedRideDisplaySegments,
  getSegmentEndpoints,
  isMixedRideWithSegments
} from "app/data/helpers/mixedRideSegmentHelpers";

const ObjectRideMap: FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { canUserViewAddressForRide } = useRidePermissions();

  // Global State
  const drawerSelectedAccountId =
    useAppSelector(selectDrawerSelectedAccountId) || "";
  const [selectedLocation, setSelectedLocation] =
    useState<types.IObjectRideLocation | null>(null);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(
    null
  );
  const [selectAccountId, setSelectedAccountId] = useState(
    drawerSelectedAccountId
  );

  const {
    ridesLoading,
    specificRide,
    objectRideLocationsLoading,
    objectRideLocations
  } = useAppSelector(selectObjectRidesState);

  // Resolve start/end plus Mixed Ride switch points (GPS points often have no address)
  const addressRows = useMemo(() => {
    const rows: any[] = specificRide ? [specificRide] : [];
    if (!isMixedRideWithSegments(specificRide)) {
      return rows;
    }

    const displaySegments = getMixedRideDisplaySegments(specificRide);
    const stops = buildMixedRideTimelineStops(
      specificRide,
      displaySegments,
      objectRideLocations
    );

    const addCoord = (
      uniqueId: string,
      latitude?: number | null,
      longitude?: number | null,
      address?: string
    ) => {
      if (address || !latitude || !longitude) return;
      rows.push({
        uniqueId,
        startLatitude: latitude,
        startLongitude: longitude
      });
    };

    stops.forEach((stop, index) => {
      addCoord(
        `mixed-stop-${index}-${stop.date}`,
        stop.latitude,
        stop.longitude,
        stop.address
      );
    });

    displaySegments
      .filter((segment) => !segment.isPrivacyHidden)
      .forEach((segment) => {
        const endpoints = getSegmentEndpoints(
          specificRide,
          segment,
          objectRideLocations
        );
        if (!endpoints) return;
        addCoord(
          `mixed-start-${segment.uniqueId}`,
          endpoints.start.latitude,
          endpoints.start.longitude,
          endpoints.start.address
        );
        addCoord(
          `mixed-end-${segment.uniqueId}`,
          endpoints.end.latitude,
          endpoints.end.longitude,
          endpoints.end.address
        );
      });

    return rows;
  }, [specificRide, objectRideLocations]);

  const { resolvedAddresses, resolvedAddressLoading } =
    useResolveAddress(addressRows);

  const fetchObjectRideData = useCallback(async () => {
    const data = {
      accountId: drawerSelectedAccountId,
      objectId: params.objectId || "",
      rideId: params.rideId || ""
    };

    dispatch(getObjectRideLocationsThunk(data));

    const { type } = await dispatch(getObjectSpecificRideInfoThunk(data));

    if (type === "objects/getObjectSpecificRideInfoThunk/rejected") {
      // Shared id: this effect can run again for the same ride, so one missing
      // ride stays one toast.
      toast.error(t("errors.objects.rideNotFound"), {
        toastId: "objects/rideNotFound"
      });
      navigate(-1);
    }
  }, [params, drawerSelectedAccountId]);

  useEffect(() => {
    if (drawerSelectedAccountId !== selectAccountId) {
      setSelectedAccountId(drawerSelectedAccountId);
      navigate("/dashboard/objects");
    }

    if (
      isEmpty(drawerSelectedAccountId) ||
      isEmpty(params.objectId) ||
      isEmpty(params.rideId)
    ) {
      toast.error(t("errors.objects.rideNotFound"), {
        toastId: "objects/rideNotFound"
      });
      navigate(-1);
    } else {
      fetchObjectRideData();
    }
  }, [params, drawerSelectedAccountId]);

  useEffect(() => {
    setSelectedSegmentId(null);
  }, [params.rideId]);

  const onLocationItemClick = (selectedData: types.IObjectRideLocation) => {
    if (selectedData?.uniqueId !== selectedLocation?.uniqueId) {
      setSelectedLocation({ ...selectedData });
      const { latitude, longitude } = selectedData;
      dispatch(getAddressThunk({ latitude, longitude }));
    }
  };

  const onSegmentSelect = (segmentId: string) => {
    setSelectedLocation(null);
    setSelectedSegmentId((current) =>
      current === segmentId ? null : segmentId
    );
  };

  return (
    <Grid container>
      {ridesLoading || objectRideLocationsLoading ? (
        <PageLoader />
      ) : (
        <Fragment>
          <Grid item xs={12} md={4}>
            <ObjectRideDetailsDrawer
              objectRide={specificRide}
              objectRideLocations={objectRideLocations}
              onLocationItemClick={onLocationItemClick}
              resolvedAddresses={resolvedAddresses}
              resolvedAddressLoading={resolvedAddressLoading}
              canViewAddressForRide={canUserViewAddressForRide}
              selectedSegmentId={selectedSegmentId}
              onSegmentSelect={onSegmentSelect}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <MapStateProvider
              height="100vh"
              disableNavigator={true}
              skipCurrentLocation={true}
              showSearch={true}
              showFilter={true}
              isDashboardMap={true}
              objectsMarker={false}
              objects={[]}
              initialZoomLevel={null}
              isHiddenLocation={
                specificRide ? hasRideHiddenAddress(specificRide) : false
              }
            >
              <ObjectRideGoogleMap
                selectedLocation={selectedLocation}
                setSelectedLocation={setSelectedLocation}
                objectRide={specificRide}
                objectRideLocations={objectRideLocations}
                onLocationItemClick={onLocationItemClick}
                selectedSegmentId={selectedSegmentId}
                resolvedAddresses={resolvedAddresses}
                onSegmentSelect={onSegmentSelect}
              />
            </MapStateProvider>
          </Grid>
        </Fragment>
      )}
    </Grid>
  );
};

export default ObjectRideMap;
